/**
 * Keeps the cart's gift lines in line with the first-order free-gift rules from the
 * 47-skin-functions app: adds a gift once its rule is met (qualifying item, minimum basket,
 * discount code) and removes it when the rule stops being met, so no gift is left at full
 * price. The `_subscription_gift` property only marks the line; the app's discount function
 * decides whether it's discounted.
 */
(function () {
  const rules = JSON.parse(document.querySelector('script[data-free-gift-rules]')?.textContent || '[]');
  const root = window.Shopify?.routes?.root || '/';
  const ADDED_KEY = 'freeGiftRulesAdded';
  let busy = false;

  const isGift = (item) => Boolean(item.properties?._subscription_gift);

  function itemQualifies(rule, item) {
    const isSubscription = Boolean(item.selling_plan_allocation);
    const typeMatches = rule.purchaseType === 'both' || (rule.purchaseType === 'subscription') === isSubscription;
    const itemMatches =
      !rule.triggerIds?.length ||
      rule.triggerIds.includes(`gid://shopify/ProductVariant/${item.variant_id}`) ||
      rule.triggerIds.includes(`gid://shopify/Product/${item.product_id}`);
    return typeMatches && itemMatches;
  }

  function ruleQualifies(rule, cart) {
    const codes = (cart.discount_codes || []).map((discount) => discount.code.toLowerCase());
    if (rule.code && !rule.autoApply && !codes.includes(rule.code.toLowerCase())) return false;

    const items = cart.items.filter((item) => !isGift(item));
    const spend = items.reduce((total, item) => total + item.final_line_price, 0);
    // The cart is in the buyer's currency, a rule's minimum basket in the shop's.
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    return spend >= rule.minimumSpend * rate && items.some((item) => itemQualifies(rule, item));
  }

  // Gifts already added this session: one the customer removed is not forced back in.
  function readAdded() {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(ADDED_KEY) || '[]'));
    } catch {
      return new Set();
    }
  }

  function post(path, body) {
    return fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async function sync(cart) {
    if (busy) return;
    busy = true;
    try {
      cart = cart?.items ? cart : await (await fetch(`${root}cart.js`)).json();
      const wanted = new Set(rules.filter((rule) => ruleQualifies(rule, cart)).map((rule) => rule.variantId));
      const giftItems = cart.items.filter(isGift);
      const missingCodes = rules
        .filter((rule) => rule.code && rule.autoApply && ruleQualifies(rule, cart))
        .map((rule) => rule.code)
        .filter((code) => !(cart.discount_codes || []).some((discount) => discount.code.toLowerCase() === code.toLowerCase()));
      if (missingCodes.length) {
        // Keeps the codes already applied
        const codes = [...(cart.discount_codes || []).map((discount) => discount.code), ...missingCodes];
        await post('cart/update.js', { discount: codes.join(',') });
        cart = await (await fetch(`${root}cart.js`)).json();
      }
      const present = new Set(giftItems.map((item) => item.variant_id));

      // A gift that stopped qualifying may be added again once it qualifies anew.
      const added = new Set([...readAdded()].filter((variantId) => wanted.has(variantId)));
      const toAdd = [...wanted].filter((variantId) => !present.has(variantId) && !added.has(variantId));
      const toRemove = giftItems.filter((item) => !wanted.has(item.variant_id));
      wanted.forEach((variantId) => {
        if (present.has(variantId) || toAdd.includes(variantId)) added.add(variantId);
      });
      try {
        sessionStorage.setItem(ADDED_KEY, JSON.stringify([...added]));
      } catch {}

      if (!toAdd.length && !toRemove.length) return;

      // The mutations render the drawer themselves, so the last one carries a drawer that already
      // holds the gift. A separate refresh could be overwritten by the stale drawer of the change
      // (e.g. the one-time/subscription toggle) that triggered this sync.
      const bundled = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: bundled } }),
      );
      const sections = bundled.join(',');

      let response;
      if (toRemove.length) {
        response = await post('cart/update.js', {
          updates: Object.fromEntries(toRemove.map((item) => [item.key, 0])),
          sections,
        });
      }
      if (toAdd.length) {
        response = await post('cart/add.js', {
          items: toAdd.map((id) => ({ id, quantity: 1, properties: { _subscription_gift: 'true' } })),
          sections,
        });
      }

      const fresh = await (await fetch(`${root}cart.js`)).json();
      fresh.sections = (await response.json()).sections;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'free-gift-rules', cart: fresh } }),
      );
    } catch (error) {
      console.warn('[Free gift rules]', error);
    } finally {
      busy = false;
    }
  }

  document.documentElement.addEventListener('cart:change', (event) => sync(event.detail?.cart));
  sync();
})();
